import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useClinicTamsFilterAPI } from "@/app/hooks/api_hooks/Inventory/useClinicTamsFilterAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useClinicToDoctorStatusListAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useClinicToDoctorStatusListAPI";
import { useDoctorListByClinicAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useDoctorListByClinicAPI";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { IClinicToDoctorListBody } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useClinicToDoctorRequestListAPI";

export interface IClinicToDoctorFiltersProps {
  senderClinics: Array<string>;
  receiverDoctors: Array<string>;
  statuses: Array<string>;
}

export const getDefaultDateRange = () => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  return {
    startDate,
    endDate,
    start_ts: Math.floor(startDate.getTime() / 1000),
    end_ts: Math.floor(endDate.getTime() / 1000),
  };
};

const TransferRequestFilters = ({ onSearch }: { onSearch: (filters: IClinicToDoctorListBody) => void }) => {
  const { execute: fetchClinicList, clinicList } = useClinicTamsFilterAPI();
  const clinicsOrgItem = useSelectMenuReducer(clinicList, "display_name", "id");
  const { execute: fetchDoctors, doctorList } = useDoctorListByClinicAPI();
  const doctorListWithEmail = doctorList.map((d) => ({
    ...d,
    nameWithEmail: d.email ? `${d.name} (${d.email})` : d.name,
  }));
  const doctorOptions = useSelectMenuReducer(doctorListWithEmail, "nameWithEmail", "user_id");
  const { execute: fetchStatuses, statusList } = useClinicToDoctorStatusListAPI();

  // Convert string[] statuses to select menu items
  const statusOptions = statusList.map((s) => ({ id: s, title: s.charAt(0).toUpperCase() + s.slice(1) }));

  const [selectionDate, setSelectionDate] = useState(() => {
    const { startDate, endDate } = getDefaultDateRange();
    return { startDate, endDate, key: "selectionDate" };
  });

  const startDate = new Date(selectionDate.startDate);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(selectionDate.endDate);
  endDate.setHours(23, 59, 59, 999);
  const startEpoch = startDate.getTime();
  const endEpoch = endDate.getTime();

  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const formHook = useForm<IClinicToDoctorFiltersProps>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    fetchStatuses();
  }, []);

  useEffect(() => {
    fetchClinicList("", "CLINIC");
  }, []);

  const handleSubmit = () => {
    onSearch({
      clinic_ids: formHook.getValues("senderClinics"),
      doctor_ids: formHook.getValues("receiverDoctors"),
      statuses: formHook.getValues("statuses"),
      start_ts: Math.floor(startEpoch / 1000),
      end_ts: Math.floor(endEpoch / 1000),
    });
  };

  return (
    <form onSubmit={formHook.handleSubmit(handleSubmit)}>
      <div className="flex flex-wrap items-end gap-3 mt-2">

        {/* Date Range */}
        <div className="w-full sm:w-auto shrink-0">
          <p className="text-[0.875rem] font-medium text-black mb-1">Date Range</p>
          <OMCalendarComponent
            selectionDate={selectionDate}
            setSelectionDate={setSelectionDate}
            startDate={fmt(startDate)}
            endDate={fmt(endDate)}
            handelClick={() => {}}
          />
        </div>

        {/* Clinic */}
        <div className="w-full sm:w-48 shrink-0">
          <p className="text-[0.875rem] font-medium text-black mb-1">Clinic</p>
          <SearchableMultiSelectMenu
            withSelectAll
            errorMessages={[]}
            onSelectItem={(item) => {
              if (item) {
                const ids = item.map((i) => i?.id);
                formHook.setValue("senderClinics", ids);
                formHook.setValue("receiverDoctors", []);
                if (ids.length > 0) fetchDoctors(ids);
              }
            }}
            fieldError={formHook?.formState?.errors?.senderClinics}
            register={formHook.register("senderClinics")}
            selectItems={clinicsOrgItem}
            placeholder="Clinic"
            showTooltips={false}
            showTypedErrors={false}
            showDropdownIcon={true}
            defaultSelected={clinicsOrgItem.filter((c) => formHook.watch("senderClinics")?.includes(c?.id))}
            className="text-gray-800"
            containerClassName="w-full"
          />
        </div>

        {/* Doctor */}
        <div className="w-full sm:w-48 shrink-0">
          <p className="text-[0.875rem] font-medium text-black mb-1">Doctor</p>
          <SearchableMultiSelectMenu
            withSelectAll
            errorMessages={[]}
            onSelectItem={(item) => {
              if (item) formHook.setValue("receiverDoctors", item.map((i) => i?.id));
            }}
            fieldError={formHook?.formState?.errors?.receiverDoctors}
            register={formHook.register("receiverDoctors")}
            selectItems={doctorOptions}
            placeholder="Doctor"
            showTooltips={false}
            showTypedErrors={false}
            showDropdownIcon={true}
            defaultSelected={doctorOptions.filter((d) => formHook.watch("receiverDoctors")?.includes(d?.id))}
            disabled={!formHook.watch("senderClinics")?.length}
            className="text-gray-800"
            containerClassName="w-full"
          />
        </div>

        {/* Status */}
        <div className="w-full sm:w-44 shrink-0">
          <p className="text-[0.875rem] font-medium text-black mb-1">Status</p>
          <SearchableMultiSelectMenu
            withSelectAll
            errorMessages={[]}
            onSelectItem={(item) => {
              if (item) formHook.setValue("statuses", item.map((i) => i?.id));
            }}
            fieldError={formHook?.formState?.errors?.statuses}
            register={formHook.register("statuses")}
            selectItems={statusOptions}
            placeholder="Select Status"
            showTooltips={false}
            showTypedErrors={false}
            showDropdownIcon={true}
            defaultSelected={statusOptions.filter((s) => formHook.watch("statuses")?.includes(s?.id))}
            className="text-gray-800"
            containerClassName="w-full"
          />
        </div>

        {/* Search */}
        <div className="w-full sm:w-auto shrink-0">
          <input
            type="submit"
            value="Search"
            className="h-[38px] w-full sm:w-auto px-6 bg-black text-white text-sm rounded-md cursor-pointer hover:bg-gray-800 transition-colors"
          />
        </div>
      </div>
    </form>
  );
};

export default TransferRequestFilters;
